import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import {unified} from 'unified'

import {HIGHLIGHT_SUBSET, useLowlight} from '../hooks/use-lowlight'
import {useAvailableAgents} from '../utils/agents-helpers'
import type {CopilotChatAgent} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {renderHastNode} from '../utils/hast-helpers'
import {userMessageParser} from '../utils/user-message/user-message-parser'
import {hasPotentialAgentMention} from '../utils/user-message/user-message-utils'
import {useChatMessage} from './ChatMessageContext'
import hljsStyles from './hljs-tokens.module.css'
import styles from './UserMessage.module.css'

interface UserMessageProps {
  className?: string
  /** Optional invisible header value for screen readers */
  accessibleHeader?: string
}

/** Renders a user message. */
export function UserMessage({className, accessibleHeader}: UserMessageProps) {
  const {message} = useChatMessage()
  const text = message.content ?? ''
  const lowlight = useLowlight(copilotFeatureFlags.prettifyPastedCode && text.includes('```'))

  // delay fetching agents until there is a potential mention
  const {availableAgents = []} = useAvailableAgents(hasPotentialAgentMention(text))

  const messageTree = unified()
    .use(userMessageParser, {
      references: message.references ?? [],
      agents: availableAgents,
      commands: [],
    })
    .parse(text)

  return (
    <>
      {accessibleHeader && <h3 className={clsx('sr-only', styles.accessibleHeader)}>{accessibleHeader}</h3>}
      <div className={clsx(styles.container, className)}>
        {messageTree.children.map((node, i) => {
          switch (node.type) {
            case 'text':
            case 'command':
              return node.value
            case 'code-block': {
              let highlightedNodes: React.ReactNode[] | null = null
              if (lowlight) {
                try {
                  const root = lowlight.highlightAuto(node.value, {subset: HIGHLIGHT_SUBSET})
                  if (root.children.some(n => n.type === 'element')) {
                    highlightedNodes = root.children.map((n, idx) => renderHastNode(n, idx))
                  }
                } catch {
                  // fall through to plain rendering
                }
              }
              return (
                <pre key={i} className={clsx(styles.codeBlock, hljsStyles.tokens, 'hljs')}>
                  <code>{highlightedNodes ?? node.value}</code>
                </pre>
              )
            }
            case 'agent-mention':
              return (
                <AgentLink key={i} agent={node.data.mentionedAgent}>
                  {node.value}
                </AgentLink>
              )
            case 'reference-mention':
              return node.data.mentionedReferenceId !== undefined ? (
                <span key={i} className={styles.mention}>
                  {node.value}
                </span>
              ) : (
                node.value
              )
          }
        })}
      </div>
    </>
  )
}

interface AgentLinkProps {
  agent: CopilotChatAgent
  children: string
}

/** Renders as link with hovercard if slug is valid; otherwise renders as plain text. */
function AgentLink({agent, children}: AgentLinkProps) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('copilot', {botSlug: agent.slug})

  return (
    <Link href={agent.integrationUrl} {...hovercardAttrs} className={styles.mention}>
      {children}
    </Link>
  )
}
